import { BaseApiService, ApiResponse, PaginatedResponse } from './baseApi';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { toast } from 'sonner';
import { Branch } from './branchApi';
import { Worker } from './workerApi';
import { RawItem } from './rawItemApi';
import { ItemCategory } from './itemCategoryApi';

// Challan Status Enum
export enum ChallanStatus {
  IN_TRANSIT = 'IN_TRANSIT',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED'
}

// Job Type Enum
export enum JobType {
  RAW_MATERIAL = 'RAW_MATERIAL',
  BOP = 'BOP',
  WIP = 'WIP',
  FINISHED_GOOD = 'FINISHED_GOOD'
}

// Challan Master interface
export interface ChallanMaster {
  id: number;
  challanNo: string;
  status: ChallanStatus;
  fromBranchId: number;
  toBranchId: number;
  jobWorkerId?: number;
  createdBy?: number;
  stateName?: string;
  vehNo?: string;
  placeOfSupp?: string;
  datetimeOfSupply?: string;
  grandTotalTax: number;
  totalAmount: number;
  grandTotal: number;
  modeOfDispatch?: string;
  documentNo?: string;
  createdOn: string;
  updatedAt?: string;
  // Relationships
  fromBranch?: Branch;
  toBranch?: Branch;
  jobWorker?: Worker;
  challanDetails?: ChallanDetail[];
}

// Challan Detail interface
export interface ChallanDetail {
  id: number;
  challanId: number;
  itemCatId?: number;
  itemId?: number;
  sku?: string;
  lotNo?: string;
  qty: number;
  itemRate?: number;
  amount?: number;
  amountTax?: number;
  taxHistoryId?: number;
  jobType?: JobType;
  createdAt: string;
  updatedAt?: string;
  // Relationships
  challanMaster?: ChallanMaster;
  itemCategory?: ItemCategory;
  rawItem?: RawItem;
}

// Create request interfaces
export interface ChallanMasterCreateRequest {
  challanNo?: string;
  status?: ChallanStatus;
  fromBranchId: number;
  toBranchId: number;
  jobWorkerId?: number;
  createdBy?: number;
  stateName?: string;
  vehNo?: string;
  placeOfSupp?: string;
  datetimeOfSupply?: string;
  grandTotalTax?: number;
  totalAmount?: number;
  grandTotal?: number;
  modeOfDispatch?: string;
  documentNo?: string;
}

export interface ChallanDetailCreateRequest {
  challanId: number;
  itemCatId?: number;
  itemId?: number;
  sku?: string;
  lotNo?: string;
  qty: number;
  itemRate?: number;
  amount?: number;
  amountTax?: number;
  taxHistoryId?: number;
  jobType?: JobType;
}

// Update request interfaces
export interface ChallanMasterUpdateRequest extends ChallanMasterCreateRequest {
  id: number;
}

export interface ChallanDetailUpdateRequest extends ChallanDetailCreateRequest {
  id: number;
}

// Filter interface
export interface ChallanFilters {
  status?: ChallanStatus;
  fromBranchId?: number;
  toBranchId?: number;
  jobWorkerId?: number;
  startDate?: string;
  endDate?: string;
}

// API Service
class ChallanApiService extends BaseApiService {
  // Get all challans with pagination and filters
  async getChallans(params?: {
    branchId?: number;
    page?: number;
    pageSize?: number;
    search?: string;
    filters?: ChallanFilters;
  }): Promise<{
    data: ChallanMaster[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      pageSize: number;
    };
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.branchId) {
        queryParams.append('branchId', params.branchId.toString());
      }
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.pageSize) {
        queryParams.append('pageSize', params.pageSize.toString());
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CHALLANS.LIST}?${queryParams.toString()}`
        : API_ENDPOINTS.CHALLANS.LIST;

      const response = await this.makeRequest<{
        success: boolean;
        data: ChallanMaster[];
        pagination: {
          currentPage: number;
          totalPages: number;
          totalCount: number;
          pageSize: number;
        };
        message?: string;
      }>(
        url,
        { method: HTTP_METHODS.GET }
      );

      if (response.success) {
        return {
          data: response.data,
          pagination: response.pagination
        };
      } else {
        throw new Error(response.message || 'Failed to fetch challans');
      }
    } catch (error) {
      // console.error('Error fetching challans:', error);
      toast.error('Failed to fetch challans');
      throw error;
    }
  }

  // Get challan by ID
  async getChallanById(id: string): Promise<ChallanMaster> {
    try {
      const response = await this.makeRequest<ApiResponse<ChallanMaster>>(
        API_ENDPOINTS.CHALLANS.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );

      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch challan');
      }
    } catch (error) {
      // console.error('Error fetching challan:', error);
      toast.error('Failed to fetch challan details');
      throw error;
    }
  }

  // Search challans
  async searchChallans(params: {
    q: string;
    branchId: number;
  }): Promise<{
    data: ChallanMaster[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      pageSize: number;
    };
  }> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('q', params.q);
      queryParams.append('branchId', params.branchId.toString());

      const url = `${API_ENDPOINTS.CHALLANS.SEARCH}?${queryParams.toString()}`;

      const response = await this.makeRequest<{
        success: boolean;
        data: ChallanMaster[];
        pagination?: {
          currentPage: number;
          totalPages: number;
          totalCount: number;
          pageSize: number;
        };
        message?: string;
      }>(
        url,
        { method: HTTP_METHODS.GET }
      );

      if (response.success) {
        const challans = response.data || [];
        const pagination = response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: challans.length,
          pageSize: challans.length
        };

        return {
          data: challans,
          pagination: pagination
        };
      } else {
        throw new Error(response.message || 'Failed to search challans');
      }
    } catch (error) {
      // console.error('Error searching challans:', error);
      toast.error('Failed to search challans');
      throw error;
    }
  }

  // Create challan
  async createChallan(data: ChallanMasterCreateRequest): Promise<ChallanMaster> {
    try {
      const response = await this.makeRequest<ApiResponse<ChallanMaster>>(
        API_ENDPOINTS.CHALLANS.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(data)
        }
      );

      if (response.success) {
        toast.success('Challan created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create challan');
      }
    } catch (error) {
      // console.error('Error creating challan:', error);
      toast.error('Failed to create challan');
      throw error;
    }
  }

  // Update challan
  async updateChallan(id: string, data: ChallanMasterUpdateRequest): Promise<ChallanMaster> {
    try {
      const response = await this.makeRequest<ApiResponse<ChallanMaster>>(
        API_ENDPOINTS.CHALLANS.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(data)
        }
      );

      if (response.success) {
        toast.success('Challan updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update challan');
      }
    } catch (error) {
      // console.error('Error updating challan:', error);
      toast.error('Failed to update challan');
      throw error;
    }
  }

  // Delete challan
  async deleteChallan(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.CHALLANS.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );

      if (response.success) {
        toast.success('Challan deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete challan');
      }
    } catch (error) {
      // console.error('Error deleting challan:', error);
      toast.error('Failed to delete challan');
      throw error;
    }
  }

  // Get challan details
  async getChallanDetails(challanId: string): Promise<ChallanDetail[]> {
    try {
      const response = await this.makeRequest<ApiResponse<ChallanDetail[]>>(
        API_ENDPOINTS.CHALLANS.DETAILS(challanId),
        { method: HTTP_METHODS.GET }
      );

      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch challan details');
      }
    } catch (error) {
      // console.error('Error fetching challan details:', error);
      toast.error('Failed to fetch challan details');
      throw error;
    }
  }

  // Create challan detail
  async createChallanDetail(data: ChallanDetailCreateRequest): Promise<ChallanDetail> {
    try {
      const response = await this.makeRequest<ApiResponse<ChallanDetail>>(
        API_ENDPOINTS.CHALLANS.CREATE_DETAIL,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(data)
        }
      );

      if (response.success) {
        toast.success('Challan detail created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create challan detail');
      }
    } catch (error) {
      // console.error('Error creating challan detail:', error);
      toast.error('Failed to create challan detail');
      throw error;
    }
  }

  // Create multiple challan details in batch
  async createChallanDetailsBatch(dataList: ChallanDetailCreateRequest[]): Promise<ChallanDetail[]> {
    try {
      const response = await this.makeRequest<ApiResponse<ChallanDetail[]>>(
        API_ENDPOINTS.CHALLANS.CREATE_DETAILS_BATCH,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(dataList)
        }
      );

      if (response.success) {
        toast.success(`${dataList.length} challan detail(s) created successfully`);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create challan details');
      }
    } catch (error) {
      // console.error('Error creating challan details in batch:', error);
      toast.error('Failed to create challan details');
      throw error;
    }
  }

  // Update challan detail
  async updateChallanDetail(id: string, data: ChallanDetailUpdateRequest): Promise<ChallanDetail> {
    try {
      const response = await this.makeRequest<ApiResponse<ChallanDetail>>(
        API_ENDPOINTS.CHALLANS.UPDATE_DETAIL(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(data)
        }
      );

      if (response.success) {
        toast.success('Challan detail updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update challan detail');
      }
    } catch (error) {
      // console.error('Error updating challan detail:', error);
      toast.error('Failed to update challan detail');
      throw error;
    }
  }

  // Delete challan detail
  async deleteChallanDetail(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.CHALLANS.DELETE_DETAIL(id),
        { method: HTTP_METHODS.DELETE }
      );

      if (response.success) {
        toast.success('Challan detail deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete challan detail');
      }
    } catch (error) {
      // console.error('Error deleting challan detail:', error);
      toast.error('Failed to delete challan detail');
      throw error;
    }
  }
}

export const challanApiService = new ChallanApiService();

